import { useEffect, useState } from "react";
import { ActionIcon, Box, Image, Loader, Stack, Text } from "@mantine/core";
import { IconDownload, IconPhotoOff } from "@tabler/icons-react";
import { spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import "./ImageTile.scss";

interface ImageTileProps {
  /** The presigned GET URL (or a data URI in preview). Null while unresolved. */
  url?: string | null;
  /** Accessible label / alt text. */
  alt: string;
  /** Column heading shown above the frame (e.g. "Source"). */
  label: string;
  /** Suggested download file name. */
  downloadName: string;
}

/**
 * A square image tile that mirrors the reviewer-app's `ImageTile`: the image is
 * `object-fit: contain` inside a 1:1 bordered frame, with a loading spinner
 * while it resolves, an error placeholder when it fails, and a download button
 * that appears on hover. Built on the design system / Mantine; no hex, spacing
 * in multiples of 4.
 */
export function ImageTile({ url, alt, label, downloadName }: ImageTileProps) {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  // Reset load state whenever the source changes.
  useEffect(() => {
    setLoaded(false);
    setError(false);
  }, [url]);

  function download() {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <Stack gap={spacing.xs}>
      <Text fw={500} size="xs" c="dimmed">
              {label}
            </Text>
      <Box className="review-tile">
        {error || !url ? (
          <div className="review-tile__status">
            {error ? (
              <IconPhotoOff size={28} aria-hidden="true" />
            ) : (
              <Loader size="sm" />
            )}
          </div>
        ) : (
          <>
            {!loaded && (
              <div className="review-tile__status">
                <Loader size="sm" />
              </div>
            )}
            <Image
              src={url}
              alt={alt}
              fit="contain"
              className="review-tile__img"
              onLoad={() => setLoaded(true)}
              onError={() => setError(true)}
            />
            {loaded && (
              <ActionIcon
                className="review-tile__download"
                variant="default"
                size="md"
                aria-label={t("review.detail.download", { label })}
                title={t("review.detail.download", { label })}
                onClick={download}
              >
                <IconDownload size={16} />
              </ActionIcon>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
}
